import React, { isValidElement } from "react";
import * as Scrivito from "scrivito";
import { Field, Form, Formik } from "formik";
import { ModalBody, ModalFooter } from "reactstrap";
import type * as Yup from "yup";
import { Button } from "components/Button";
import { TranslateWrapper } from "components/TranslateWrapper";
import { FormSelect, TextInput } from "components/Inputs";
import cx from "classnames";
import { useTranslate } from "hooks";
import type { ProjectStatuses } from "hooks/useProjectStatuses";
import { DepartmentSelect } from "components/_Modals/ProjectDetailsModal/DepartmentSelect";
import s from "./ProjectDetailsModal.module.scss";
import { useIsSameProj } from "stores/globalStore";
import type {
  SelectOption,
  TranslateWrapperProps,
} from "interfaces/shared.types";
import type { Department } from "stores/user/userStore";

export type StatusOption = SelectOption & { color: string };

export type FormValues = {
  title?: string | null;
  constructionTitle?: string | null;
  projectNumber?: string | null;
  description?: string | null;
  status?: StatusOption;
  country?: SelectOption;
  mandator?: SelectOption<number>;
  units?: SelectOption;
  departmentId?: Department | null;
};

type Props = {
  handleSubmit: (values: FormValues) => void;
  initialValues: FormValues;
  isUpdate: boolean;
  countriesOptions: SelectOption[] | undefined;
  mandatorOptions: SelectOption<number>[];
  optionsList: StatusOption[] | undefined;
  unitOptions: SelectOption[];
  validationSchema: Yup.ObjectSchema<object>;
  projectStatuses?: ProjectStatuses;
  projectExists?: boolean;
};

export const ProjectDetailsForm = Scrivito.connect(
  ({
    handleSubmit,
    initialValues,
    isUpdate,
    countriesOptions,
    mandatorOptions,
    optionsList,
    unitOptions,
    validationSchema,
  }: Props) => {
    const translate = useTranslate();
    const isSameProj = useIsSameProj();
    const translated = translate("PROJECT_DESCRIPTION_INPUT_PLACEHOLDER");
    const tooltipText = isValidElement<TranslateWrapperProps>(translated)
      ? translated.props.tooltipText
      : "";

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur={false}
      >
        <Form>
          <ModalBody className="pt-2_5 d-flex row">
            <div className="col-12 col-lg-6 order-1">
              {isSameProj && !isUpdate && <DepartmentSelect />}
              <Field
                component={TextInput}
                name="title"
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_NAME_WITH_ASTERISK")}
                data-testid="project-name-field"
                maxLength={100}
              />
              <Field
                component={TextInput}
                name="constructionTitle"
                wrapperClassName="mt-3"
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_CONSTRUCTION_TITLE_INPUT_LABEL")}
                tooltipText={translate(
                  "PROJECT_CONSTRUCTION_TITLE_TOOLTIP_TEXT",
                )}
                maxLength={100}
              />
              <Field
                component={FormSelect}
                name="country"
                wrapperClassName={cx(s.fieldWithoutMarginBottom, "mt-3")}
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_LOCATION_WITH_ASTERISK")}
                tooltipText={translate("PROJECT_LOCATION_SELECT_TOOLTIP_TEXT")}
                options={countriesOptions}
              />
              {isSameProj && (
                <Field
                  component={FormSelect}
                  name="mandator"
                  wrapperClassName={cx(s.fieldWithoutMarginBottom, "mt-3")}
                  autoFocus={true}
                  data-recording-disable={true}
                  label={translate("TROX_BUSINESS_PARTNERS")}
                  tooltipText={translate("TROX_BUSINESS_PARTNERS_TOOLTIP")}
                  options={mandatorOptions}
                  disabled={isUpdate}
                />
              )}
            </div>
            <div className="col-12 col-lg-6 flex-grow-1 order-3">
              <Field
                component={FormSelect}
                name="units"
                wrapperClassName="mt-3"
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_UNITS_WITH_ASTERISK")}
                tooltipText={translate("PROJECT_UNITS_SELECT_TOOLTIP_TEXT")}
                options={unitOptions}
                disabled={true}
              />
              <Field
                component={TextInput}
                name="projectNumber"
                wrapperClassName="mt-3"
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_NUMBER")}
                tooltipText={translate("PROJECT_NUMBER_TOOLTIP_TEXT")}
                maxLength={100}
              />
            </div>
            <div className="col-12 col-lg-6 flex-grow-1 order-2">
              <Field
                id="project-description-input"
                component={TextInput}
                type="textarea"
                name="description"
                autoFocus={true}
                data-recording-disable={true}
                placeholder={translate.stringOnly(
                  "PROJECT_DESCRIPTION_INPUT_PLACEHOLDER",
                  [],
                )}
                label={translate("PROJECT_DESCRIPTION_INPUT_LABEL")}
                wrapperClassName={cx(
                  s.fieldWithoutMarginBottom,
                  "h-100 mb-3 pb-2 d-flex flex-column",
                )}
                maxLength={500}
                className="d-flex flex-grow-1"
              />
            </div>
            {typeof translate("PROJECT_DESCRIPTION_INPUT_PLACEHOLDER") ===
              "object" && (
              <TranslateWrapper
                tooltipId="project-description-input"
                tooltipText={tooltipText}
              />
            )}
            <div className="col-12 col-lg-6 flex-grow-1 order-4">
              <Field
                component={FormSelect}
                name="status"
                wrapperClassName="mt-3"
                autoFocus={true}
                data-recording-disable={true}
                label={translate("PROJECT_STATUS")}
                tooltipText={translate("PROJECT_STATUS_SELECT_TOOLTIP_TEXT")}
                options={optionsList}
              />
              <div className="d-flex justify-content-end mt-4">
                {translate("REQUIRED_FIELD_WITH_ASTERISK")}
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="pt-0">
            <Button
              data-testid="ProjectDetailsForm_submit-btn"
              color="primary"
              type="submit"
            >
              {isUpdate
                ? translate("TEXT_SAVE_CHANGES")
                : translate("PROJECT_MODAL_CREATE_BUTTON_TEXT")}
            </Button>
          </ModalFooter>
        </Form>
      </Formik>
    );
  },
);
